import React, { Ref, useCallback, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";

import { duration } from "@formant/core/model/duration";
import { IRtcStream } from "@formant/core/model/IRtcStream";
import { IRtcStreamMessage } from "@formant/rtc-client/models/IRtcStreamMessage";
import { rtcPipelineType } from "@formant/rtc-client/models/IStreamControl";

import { ITeleopModuleProps } from "..";
import { useStores } from "../../../../context/StoreContext";
import {
    Container,
    ImageDisplay,
    NoSignal,
    NoSignalIcon,
    TeleopScreenModule,
} from "./TeleopScreen.styles";

const blankImgSrc = "data:,";

export interface ITeleopScreenProps extends ITeleopModuleProps {
    stream: IRtcStream;
    boundsRef: Ref<HTMLElement>;
    primary?: boolean;
}

export const TeleopScreen = observer(function TeleopScreen(
    props: ITeleopScreenProps,
) {
    const {
        boundsRef,
        primary,
        session,
        stream: { streamName },
    } = props;

    const { operateStore } = useStores();

    const [measureEl] = useState<HTMLImageElement>(() => new Image());
    const noDataTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [recentVideoSignal, setRecentVideoSignal] = useState(false);
    const [src, setSrc] = useState(blankImgSrc);

    const handleReceiveImage = useCallback(
        (msg: IRtcStreamMessage) => {
            const { payload } = msg;
            const { compressedImage: imagePayload } = payload;

            if (!imagePayload) {
                return;
            }

            setRecentVideoSignal(true);

            const srcUrl = `data:image/jpg;base64,${imagePayload.data}`;
            setSrc(srcUrl);

            // Enqueue content dimensions if primary view
            measureEl.src = srcUrl;

            // Cancel previous noData timeout
            if (noDataTimeout.current) {
                clearTimeout(noDataTimeout.current);
                noDataTimeout.current = null;
            }

            noDataTimeout.current = setTimeout(() => {
                setSrc(blankImgSrc);
                setRecentVideoSignal(false);
                session.disableControls();
                operateStore.contentDimensions = undefined;
            }, duration.second * 4);
        },
        [
            measureEl,
            noDataTimeout,
            operateStore,
            session,
            setRecentVideoSignal,
            setSrc,
        ],
    );

    useEffect(() => {
        const unregister = session.registerReceiver(
            streamName,
            handleReceiveImage,
        );

        if (session.connectionStatus === "connected") {
            session.controlRemoteStream({
                streamName,
                enable: true,
                pipeline: rtcPipelineType,
            });
        }

        return () => {
            unregister();
        };
    }, [session, streamName, handleReceiveImage]);

    // Measure decoded image
    useEffect(() => {
        if (!primary) {
            return;
        }

        const handleMeasure = () => {
            operateStore.contentDimensions = {
                x: measureEl.naturalWidth,
                y: measureEl.naturalHeight,
            };
        };

        measureEl.addEventListener("load", handleMeasure);
        return () => {
            measureEl.removeEventListener("load", handleMeasure);
            operateStore.contentDimensions = undefined;
        };
    }, [measureEl, operateStore, primary]);

    useEffect(() => {
        return () => {
            // Cleanup trailing noData timeout
            if (noDataTimeout.current) {
                clearTimeout(noDataTimeout.current);
                noDataTimeout.current = null;
            }
        };
    }, [noDataTimeout]);

    return (
        <TeleopScreenModule session={session}>
            {recentVideoSignal ? (
                <Container fill={operateStore.fill}>
                    <ImageDisplay
                        src={src}
                        ref={boundsRef as Ref<HTMLImageElement>}
                        alt={streamName}
                    />
                </Container>
            ) : (
                <NoSignal>
                    <NoSignalIcon name="video-off" />
                    Waiting for signal
                </NoSignal>
            )}
        </TeleopScreenModule>
    );
});
